import React from 'react';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import MapSelector from '../../Components/common/MapSelector';
import Loader from '../../Components/common/loader/loader';
import ConfirmationModal from '../../Components/common/ConfirmationModal';
import { CreatePlantTabs, CustomerSelectField, SiteSelectField } from './CreatePlantFormFields';
import { useCreatePlantForm } from './useCreatePlantForm';
import './createPlant.css';

export type { CustomerLite } from './createPlantHelpers';

const CreatePlant: React.FC = () => {
  const form = useCreatePlantForm();

  const nextButtonLabel = (() => {
    if (form.loading) {
      return 'Next';
    }
    return form.hasPlantDataChanged() ? 'Save & Next' : 'Next';
  })();

  return (
    <>
      {form.loading && <Loader isOverlay={true} />}
      <ConfirmationModal
        isOpen={form.showDeleteModal}
        title="Delete Plant"
        message={`Are you sure you want to delete the plant "${form.plantInfo.plantName}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={form.confirmDeletePlant}
        onCancel={form.cancelDeletePlant}
        isDestructive={true}
      />
      <div className="create-plant-container" style={{ position: 'relative' }}>
        <div className="create-plant-header" />

        <CreatePlantTabs
          currentStep={form.currentStep}
          mode={form.mode}
          plantId={form.plantId}
          assetsCount={form.assets.length}
          onTabNavigation={form.handleTabNavigation}
        />

        <div className="plant-information-section">
          <h3>Plant Information</h3>
          <div className="content-layout">
            <div className="form-container">
              <div className="form-group">
                <label htmlFor="plant-customer">
                  Customer<span className="required">*</span>
                </label>
                <CustomerSelectField
                  loading={form.loading}
                  isEditMode={Boolean(form.plantId)}
                  plantCustomerName={form.plantInfo.customer}
                  isDropdownOpen={form.isCustomerDropdownOpen}
                  customerSearchText={form.customerSearchText}
                  selectedCustomer={form.selectedCustomer}
                  filteredCustomers={form.filteredCustomers}
                  onToggleDropdown={form.toggleCustomerDropdown}
                  onSearchChange={form.setCustomerSearchText}
                  onOpenDropdownFromKeyboard={() => form.setIsCustomerDropdownOpen(true)}
                  onSelectCustomer={form.handleCustomerSelect}
                />
              </div>

              <div className="form-group">
                <label htmlFor="plant-site">
                  Site<span className="required">*</span>
                </label>
                <SiteSelectField
                  loading={form.loading}
                  isEditMode={Boolean(form.plantId)}
                  siteValue={form.plantInfo.site}
                  isDropdownOpen={form.isSiteDropdownOpen}
                  siteSearchText={form.siteSearchText}
                  selectedCustomer={form.selectedCustomer}
                  filteredSites={form.filteredSites}
                  onToggleDropdown={form.toggleSiteDropdown}
                  onSearchChange={form.setSiteSearchText}
                  onOpenDropdownFromKeyboard={() => form.setIsSiteDropdownOpen(true)}
                  onSelectSite={form.handleSiteSelect}
                />
              </div>

              <div className="form-group">
                <label htmlFor="plant-name">
                  Plant Name<span className="required">*</span>
                </label>
                <input
                  id="plant-name"
                  type="text"
                  name="plantName"
                  value={form.plantInfo.plantName}
                  onChange={form.handleInputChange}
                  placeholder="Enter plant name"
                  disabled={form.loading}
                />
              </div>

              <div className="form-group">
                <label htmlFor="plant-status">Status</label>
                <select
                  id="plant-status"
                  name="status"
                  value={form.plantInfo.status}
                  onChange={form.handleInputChange}
                  disabled={form.loading}
                >
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
              </div>

              <div className="form-group" style={{ marginBottom: '16px' }}>
                <label htmlFor="plant-coordinates">Coordinates</label>
                <input
                  id="plant-coordinates"
                  type="text"
                  value={form.coordinatesDisplay}
                  onChange={form.handleCoordinatesChange}
                  placeholder="Enter Latitude, Longitude (e.g., 40.7128, -74.0060)"
                  disabled={form.loading}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '14px',
                  }}
                />
              </div>

              <div className="button-container" style={{ position: 'relative', zIndex: 1000 }}>
                <button
                  type="button"
                  className="cancel-button-plant"
                  onClick={form.handleCancel}
                  disabled={form.loading}
                >
                  Cancel
                </button>
                {form.plantId && (
                  <button
                    type="button"
                    className="delete-button"
                    onClick={form.handleDeletePlant}
                    disabled={form.loading || form.deleting}
                  >
                    {form.deleting ? 'Deleting...' : 'Delete Plant'}
                  </button>
                )}
                {form.mode === 'create' ? (
                  <button
                    type="button"
                    className="next-button"
                    onClick={form.handleSaveAndNext}
                    disabled={form.loading}
                    style={{
                      cursor: 'pointer',
                      pointerEvents: 'auto',
                      position: 'relative',
                      zIndex: 1001,
                      backgroundColor: '#0094BD',
                      border: 'none',
                      color: 'white',
                      width: 'auto',
                      minWidth: 'fit-content',
                      whiteSpace: 'nowrap',
                      padding: '8px 16px',
                    }}
                  >
                    {form.loading ? 'Saving...' : 'Save & Next'}
                  </button>
                ) : (
                  <button
                    type="button"
                    className="next-button"
                    onClick={form.handleNext}
                    disabled={form.loading}
                    style={{
                      cursor: 'pointer',
                      pointerEvents: 'auto',
                      position: 'relative',
                      zIndex: 1001,
                      backgroundColor: '#0094BD',
                    }}
                  >
                    {nextButtonLabel}
                  </button>
                )}
              </div>
            </div>

            <div className="map-container">
              <div className="site-location-container">
                <h4>Plant Location</h4>
                <div className="map-wrapper">
                  <MapSelector
                    latitude={form.plantInfo.latitude}
                    longitude={form.plantInfo.longitude}
                    onLocationSelect={form.handleMapLocationSelect}
                    height="280px"
                    disabled={form.loading}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <ToastContainer
          className="toastify-message-plant"
          position="top-right"
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          style={{ zIndex: 9999 }}
        />
      </div>
    </>
  );
};

export default CreatePlant;
